import { useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    MoreVert as MoreIcon,
} from '@mui/icons-material';
import { SttButtonDelete, SttButtonSave, SttButton } from '../all';
import { usePermission } from '../../lib/usePermission';
import { PermissionRequirement } from '../../lib/permissions';

type ButtonProps = {
    show?: boolean;
    type?: 'delete' | 'save' | 'custom';
    onClick: () => void;
    caption?: string;
    icon?: ReactNode;
    /** Permiso v8 que hace falta para ver el botón (p. ej. `<módulo>.modify`). */
    permission?: PermissionRequirement;
};

type MenuItemProps = {
    show?: boolean;
    caption: string;
    onClick?: () => void;
    icon?: ReactNode;
    divider?: boolean;
};

type SttFullDialogProps = {
    open?: boolean;
    title?: string | React.ReactElement;
    onClose?: () => void;
    buttons?: ButtonProps[];
    children?: ReactNode;
    menu?: MenuItemProps[];
    bottomButtons?: ButtonProps[];
    bottomContent?: ReactNode;
    urlPath?: string;
};

export function SttFullDialog({
    open = false,
    title,
    onClose,
    buttons,
    children,
    menu,
    bottomButtons,
    bottomContent,
    urlPath,
}: SttFullDialogProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [buttonMenuDOMObject, setButtonMenuDOMObject] =
        useState<HTMLElement | null>(null);
    const previousPathRef = useRef<string | null>(null);
    const { can } = usePermission();
    const isAllowed = ({ show = true, permission }: ButtonProps) =>
        show && (!permission || can(permission));
    const hasNavigatedRef = useRef(false);

    // Handle URL updates when dialog opens/closes
    useEffect(() => {
        if (open && urlPath) {
            // Store the current path before navigating
            if (!hasNavigatedRef.current) {
                previousPathRef.current = location.pathname + location.search;
                hasNavigatedRef.current = true;
                // Push the new URL
                navigate(urlPath, { replace: false });
            }
        } else if (!open && hasNavigatedRef.current) {
            hasNavigatedRef.current = false;
            // Only navigate back if we're still on the dialog URL
            if (location.pathname === urlPath) {
                navigate(previousPathRef.current || '..', { replace: true });
            }
            previousPathRef.current = null;
        }
    }, [open, urlPath, navigate, location.pathname, location.search]);

    // Handle browser back button
    useEffect(() => {
        if (!open || !urlPath) return;

        const handlePopState = () => {
            // If user presses back button while dialog is open, close it
            if (hasNavigatedRef.current) {
                hasNavigatedRef.current = false;
                previousPathRef.current = null;
                onClose?.();
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [open, urlPath, onClose]);

    const handleClose = () => {
        onClose?.();
    };

    const renderButton = (button: ButtonProps, idx: number) => {
        const { type, onClick, caption, icon } = button;

        if (!isAllowed(button)) {
            return null;
        }

        let content: ReactNode;
        switch (type) {
            case 'delete':
                content = (
                    <SttButtonDelete onClick={onClick} caption={caption} />
                );
                break;

            case 'save':
                content = <SttButtonSave onClick={onClick} />;
                break;

            default:
                content = (
                    <SttButton
                        caption={caption}
                        onClick={onClick}
                        icon={icon}
                    />
                );
                break;
        }

        return (
            <Box key={idx} mx={1}>
                {content}
            </Box>
        );
    };

    const renderMenu = (
        <Box mx={1}>
            <IconButton
                color="inherit"
                onClick={(ev: any) => setButtonMenuDOMObject(ev.currentTarget)}
            >
                <MoreIcon />
            </IconButton>
            <Menu
                anchorEl={buttonMenuDOMObject}
                open={buttonMenuDOMObject !== null}
                onClose={() => setButtonMenuDOMObject(null)}
            >
                {menu
                    ?.filter(({ show = true }: any) => show)
                    .map((item: any, idx: any) => (
                        <div key={idx}>
                            {item.divider && <Divider />}
                            <MenuItem
                                onClick={() => {
                                    item.onClick?.();
                                    setButtonMenuDOMObject(null);
                                }}
                            >
                                {item.icon && (
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                )}
                                {item.caption}
                            </MenuItem>
                        </div>
                    ))}
            </Menu>
        </Box>
    );

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            disableRestoreFocus
            slotProps={{
                // El scroll vive en el contenido, no en el paper: así la
                // cabecera ocupa el ancho completo y la barra de scroll no la
                // recorta por la derecha.
                paper: {
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            {/* Cabecera al estilo del layout nuevo: superficie neutra con borde
                inferior y el título como en la cabecera de página, sin barra de
                color. Va fuera del área que hace scroll (ver PaperProps) para
                que ocupe todo el ancho. */}
            <Box
                component="header"
                sx={{
                    flexShrink: 0,
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                }}
            >
                <IconButton
                    edge="start"
                    onClick={handleClose}
                    aria-label="close"
                    size="small"
                    className="border-border border"
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography
                    noWrap
                    component="h2"
                    sx={{
                        flex: 1,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>

                {buttons?.map((bt, i) => renderButton(bt, i))}

                {menu && renderMenu}
            </Box>

            <DialogContent
                sx={{
                    backgroundColor: 'background.default',
                    flex: 1,
                    overflowY: 'auto',
                }}
            >
                {children}
            </DialogContent>

            {(bottomButtons || bottomContent) && (
                <DialogActions
                    sx={{
                        justifyContent: 'flex-start',
                        gap: 1,
                        pl: 10,
                        pr: 2,
                        py: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    {bottomContent}
                    {bottomButtons?.filter(isAllowed).map((button, i) => {
                        const { type, onClick, caption, icon } = button;

                        switch (type) {
                            case 'delete':
                                return (
                                    <SttButtonDelete
                                        key={i}
                                        onClick={onClick}
                                        caption={caption}
                                    />
                                );
                            case 'save':
                                return (
                                    <SttButtonSave key={i} onClick={onClick} />
                                );
                            default:
                                return (
                                    <SttButton
                                        key={i}
                                        caption={caption}
                                        onClick={onClick}
                                        icon={icon}
                                    />
                                );
                        }
                    })}
                </DialogActions>
            )}
        </Dialog>
    );
}
